import JSZip from "jszip";
import type {
  Workspace,
  Team,
  Issue,
  Project,
  Doc,
  ProgressCard,
  User,
  Label,
  Cycle,
} from "./types";
import { STATUSES, PRIORITIES, PROJECT_STATUSES } from "./constants";
import { humanOwnerId } from "./taskOwnership";

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 64) || "untitled";
}

function isoDate(ts: number | null | undefined) {
  if (!ts) return "";
  return new Date(ts).toISOString().slice(0, 10);
}

function frontmatter(data: Record<string, string | string[] | number | null | undefined>) {
  const lines: string[] = ["---"];
  for (const [key, val] of Object.entries(data)) {
    if (val == null || val === "") continue;
    if (Array.isArray(val)) {
      if (val.length === 0) continue;
      lines.push(`${key}:`);
      for (const v of val) lines.push(`  - ${yamlStr(String(v))}`);
    } else {
      lines.push(`${key}: ${yamlStr(String(val))}`);
    }
  }
  lines.push("---", "");
  return lines.join("\n");
}

function yamlStr(s: string) {
  if (/[:#\[\]{}&*!|>'"%@`,\n]/.test(s) || /^\s|\s$/.test(s)) {
    return JSON.stringify(s);
  }
  return s;
}

interface ExportInput {
  workspace: Workspace;
  teams: Team[];
  labels: Label[];
  issues: Issue[];
  projects: Project[];
  cycles: Cycle[];
  docs: Doc[];
  progressCards: ProgressCard[];
  users: User[];
}

export async function exportWorkspaceZip(data: ExportInput) {
  const { workspace, teams, labels, issues, projects, cycles, docs, progressCards, users } = data;
  const zip = new JSZip();
  const root = zip.folder(slugify(workspace.name))!;

  // README.md — workspace root index
  const readme: string[] = [];
  readme.push(frontmatter({
    title: workspace.name,
    type: "workspace",
    workspace: workspace.name,
    tags: ["workspace", workspace.slug],
    created: isoDate(workspace.createdAt),
    exported: isoDate(Date.now()),
  }));
  readme.push(`# ${workspace.icon} ${workspace.name}\n`);
  if (workspace.description) readme.push(`> ${workspace.description}\n`);
  readme.push(`## Contents`);
  readme.push(`- [[${slugify("tasks-index")}|Tasks]] — ${issues.length}`);
  readme.push(`- [[${slugify("projects-index")}|Projects]] — ${projects.length}`);
  readme.push(`- [[${slugify("docs-index")}|Docs]] — ${docs.length}`);
  readme.push(`- [[${slugify("progress-index")}|Progress Cards]] — ${progressCards.length}`);
  readme.push(`- [[${slugify("teams-index")}|Teams]] — ${teams.length}`);
  readme.push("");
  readme.push(`## Teams`);
  for (const t of teams) readme.push(`- **${t.name}** (${t.key}) — ${t.description || ""}`);
  readme.push("");
  readme.push(`*Exported from Pauta on ${new Date().toLocaleString()}*`);
  root.file("README.md", readme.join("\n"));

  // Issues folder
  const issuesFolder = root.folder("tasks")!;
  const issuesIndex: string[] = [];
  issuesIndex.push(frontmatter({ title: "Tasks Index", type: "index", tags: ["index", "tasks"] }));
  issuesIndex.push(`# Tasks · ${workspace.name}\n`);
  for (const s of STATUSES) {
    const group = issues.filter((i) => i.status === s.id);
    if (!group.length) continue;
    issuesIndex.push(`## ${s.name} (${group.length})`);
    for (const iss of group) {
      issuesIndex.push(`- [[${issueFilename(iss, false)}|${iss.identifier}]] ${iss.title}`);
    }
    issuesIndex.push("");
  }
  issuesFolder.file("_index.md", issuesIndex.join("\n"));

  for (const iss of issues) {
    const team = teams.find((t) => t.id === iss.teamId);
    const project = projects.find((p) => p.id === iss.projectId);
    const assignee = users.find((u) => u.id === humanOwnerId(iss));
    const issueLabels = labels.filter((l) => iss.labelIds.includes(l.id));
    const priority = PRIORITIES.find((p) => p.id === iss.priority);
    const status = STATUSES.find((s) => s.id === iss.status);
    const body: string[] = [];
    body.push(
      frontmatter({
        id: iss.identifier,
        title: iss.title,
        type: "issue",
        status: status?.name,
        priority: priority?.name,
        assignee: assignee?.name,
        team: team?.name,
        project: project?.name,
        labels: issueLabels.map((l) => l.name),
        due: isoDate(iss.dueDate),
        estimate: iss.estimate ?? undefined,
        created: isoDate(iss.createdAt),
        updated: isoDate(iss.updatedAt),
        tags: ["task", slugify(team?.name || ""), status ? slugify(status.name) : ""],
      })
    );
    body.push(`# ${iss.identifier} · ${iss.title}\n`);
    body.push(`**Status**: ${status?.name} · **Priority**: ${priority?.name} · **Assignee**: ${assignee?.name || "Unassigned"}\n`);
    if (project) body.push(`**Project**: [[${projectFilename(project, false)}|${project.name}]]`);
    if (team) body.push(`**Team**: ${team.name}`);
    if (issueLabels.length)
      body.push(`**Labels**: ${issueLabels.map((l) => `#${slugify(l.name)}`).join(" ")}`);
    body.push("");
    body.push(`## Description\n`);
    body.push(iss.description || "_No description._");
    if (iss.comments.length) {
      body.push(`\n## Comments\n`);
      for (const c of iss.comments) {
        const u = users.find((x) => x.id === c.userId);
        body.push(`**${u?.name || "Unknown"}** · ${new Date(c.createdAt).toLocaleString()}\n`);
        body.push(c.body);
        body.push("");
      }
    }
    issuesFolder.file(issueFilename(iss, true), body.join("\n"));
  }

  // Projects folder
  const projectsFolder = root.folder("projects")!;
  const projIndex: string[] = [];
  projIndex.push(frontmatter({ title: "Projects Index", type: "index", tags: ["index", "projects"] }));
  projIndex.push(`# Projects · ${workspace.name}\n`);
  for (const p of projects) {
    const st = PROJECT_STATUSES.find((s) => s.id === p.status);
    projIndex.push(`- [[${projectFilename(p, false)}|${p.icon} ${p.name}]] — ${st?.name}`);
  }
  projectsFolder.file("_index.md", projIndex.join("\n"));

  for (const p of projects) {
    const lead = users.find((u) => u.id === p.leadId);
    const pTeams = teams.filter((t) => p.teamIds.includes(t.id));
    const pIssues = issues.filter((i) => i.projectId === p.id);
    const done = pIssues.filter((i) => i.status === "done").length;
    const progress = pIssues.length ? Math.round((done / pIssues.length) * 100) : 0;
    const st = PROJECT_STATUSES.find((s) => s.id === p.status);

    const body: string[] = [];
    body.push(
      frontmatter({
        title: p.name,
        type: "project",
        status: st?.name,
        lead: lead?.name,
        teams: pTeams.map((t) => t.name),
        start: isoDate(p.startDate),
        target: isoDate(p.targetDate),
        progress: `${progress}%`,
        tags: ["project", slugify(p.name)],
      })
    );
    body.push(`# ${p.icon} ${p.name}\n`);
    if (p.description) body.push(`> ${p.description}\n`);
    body.push(`**Status**: ${st?.name} · **Lead**: ${lead?.name || "—"} · **Progress**: ${progress}% (${done}/${pIssues.length})\n`);
    if (p.targetDate) body.push(`**Target**: ${isoDate(p.targetDate)}\n`);
    body.push(`## Teams`);
    for (const t of pTeams) body.push(`- ${t.name}`);
    body.push("");
    body.push(`## Tasks`);
    for (const iss of pIssues) {
      body.push(`- [[${issueFilename(iss, false)}|${iss.identifier}]] ${iss.title} — ${STATUSES.find((s) => s.id === iss.status)?.name}`);
    }
    body.push("");
    const relatedDocs = docs.filter((d) => d.projectId === p.id);
    if (relatedDocs.length) {
      body.push(`## Docs`);
      for (const d of relatedDocs)
        body.push(`- [[${docFilename(d, false)}|${d.icon} ${d.title}]]`);
      body.push("");
    }
    const relatedCards = progressCards.filter((c) => c.projectId === p.id);
    if (relatedCards.length) {
      body.push(`## Progress Cards`);
      for (const c of relatedCards)
        body.push(`- [[${progressFilename(c, false)}|${c.title}]] — ${c.status}`);
    }
    projectsFolder.file(projectFilename(p, true), body.join("\n"));
  }

  // Docs folder
  const docsFolder = root.folder("docs")!;
  const docsIndex: string[] = [];
  docsIndex.push(frontmatter({ title: "Docs Index", type: "index", tags: ["index", "docs"] }));
  docsIndex.push(`# Docs · ${workspace.name}\n`);
  for (const d of docs) {
    docsIndex.push(`- [[${docFilename(d, false)}|${d.icon} ${d.title}]]`);
  }
  docsFolder.file("_index.md", docsIndex.join("\n"));

  for (const d of docs) {
    const project = projects.find((p) => p.id === d.projectId);
    const team = teams.find((t) => t.id === d.teamId);
    const author = users.find((u) => u.id === d.authorId);
    const body: string[] = [];
    body.push(
      frontmatter({
        title: d.title,
        type: "doc",
        project: project?.name,
        team: team?.name,
        author: author?.name,
        pinned: d.pinned ? "true" : undefined,
        created: isoDate(d.createdAt),
        updated: isoDate(d.updatedAt),
        tags: ["doc", ...d.tags],
      })
    );
    body.push(d.content);
    docsFolder.file(docFilename(d, true), body.join("\n"));
  }

  // Progress folder
  const progressFolder = root.folder("progress")!;
  const progIndex: string[] = [];
  progIndex.push(frontmatter({ title: "Progress Cards Index", type: "index", tags: ["index", "progress"] }));
  progIndex.push(`# Progress Cards · ${workspace.name}\n`);
  for (const c of progressCards) {
    const project = projects.find((p) => p.id === c.projectId);
    progIndex.push(
      `- [[${progressFilename(c, false)}|${c.title}]]${project ? ` — ${project.name}` : ""} (${c.status})`
    );
  }
  progressFolder.file("_index.md", progIndex.join("\n"));

  for (const c of progressCards) {
    const project = projects.find((p) => p.id === c.projectId);
    const author = users.find((u) => u.id === c.authorId);
    const linked = issues.filter((i) => c.issueIds.includes(i.id));
    const body: string[] = [];
    body.push(
      frontmatter({
        title: c.title,
        type: "progress-card",
        status: c.status,
        project: project?.name,
        author: author?.name,
        period_start: isoDate(c.periodStart),
        period_end: isoDate(c.periodEnd),
        created: isoDate(c.createdAt),
        updated: isoDate(c.updatedAt),
        tags: ["progress", slugify(project?.name || "workspace")],
      })
    );
    body.push(`# ${c.title}\n`);
    body.push(
      `**Period**: ${isoDate(c.periodStart)} → ${isoDate(c.periodEnd)}${
        project ? ` · **Project**: [[${projectFilename(project, false)}|${project.name}]]` : ""
      }\n`
    );
    body.push(`## Summary\n\n${c.summary || "_No summary._"}\n`);
    if (c.metrics.length) {
      body.push(`## Metrics\n`);
      body.push(`| Metric | Value | Change |`);
      body.push(`| --- | --- | --- |`);
      for (const m of c.metrics)
        body.push(`| ${m.label} | ${m.value} | ${m.change || "—"} |`);
      body.push("");
    }
    if (c.highlights.length) {
      body.push(`## Highlights\n`);
      for (const h of c.highlights) body.push(`- ✅ ${h}`);
      body.push("");
    }
    if (c.blockers.length) {
      body.push(`## Blockers\n`);
      for (const b of c.blockers) body.push(`- ⚠️ ${b}`);
      body.push("");
    }
    if (c.nextSteps.length) {
      body.push(`## Next Steps\n`);
      for (const n of c.nextSteps) body.push(`- → ${n}`);
      body.push("");
    }
    if (linked.length) {
      body.push(`## Linked Tasks\n`);
      for (const iss of linked)
        body.push(`- [[${issueFilename(iss, false)}|${iss.identifier}]] ${iss.title}`);
      body.push("");
    }
    progressFolder.file(progressFilename(c, true), body.join("\n"));
  }

  // Teams folder
  const teamsFolder = root.folder("teams")!;
  const teamsIndex: string[] = [];
  teamsIndex.push(frontmatter({ title: "Teams Index", type: "index", tags: ["index", "teams"] }));
  teamsIndex.push(`# Teams · ${workspace.name}\n`);
  for (const t of teams) teamsIndex.push(`- [[${teamFilename(t, false)}|${t.name}]]`);
  teamsFolder.file("_index.md", teamsIndex.join("\n"));

  for (const t of teams) {
    const tIssues = issues.filter((i) => i.teamId === t.id);
    const tCycles = cycles.filter((c) => c.teamId === t.id);
    const body: string[] = [];
    body.push(
      frontmatter({
        title: t.name,
        type: "team",
        key: t.key,
        tags: ["team", slugify(t.name)],
      })
    );
    body.push(`# ${t.icon} ${t.name} (${t.key})\n`);
    if (t.description) body.push(`> ${t.description}\n`);
    body.push(`## Tasks (${tIssues.length})`);
    for (const iss of tIssues)
      body.push(`- [[${issueFilename(iss, false)}|${iss.identifier}]] ${iss.title}`);
    body.push("");
    if (tCycles.length) {
      body.push(`## Cycles`);
      for (const c of tCycles)
        body.push(
          `- **${c.name}** — ${isoDate(c.startDate)} → ${isoDate(c.endDate)}`
        );
    }
    teamsFolder.file(teamFilename(t, true), body.join("\n"));
  }

  // Produce ZIP
  const blob = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${slugify(workspace.name)}-obsidian-vault.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function issueFilename(iss: Issue, withExt: boolean) {
  const base = `${iss.identifier}-${slugify(iss.title)}`;
  return withExt ? `${base}.md` : base;
}
function projectFilename(p: Project, withExt: boolean) {
  const base = slugify(p.name);
  return withExt ? `${base}.md` : base;
}
function docFilename(d: Doc, withExt: boolean) {
  const base = slugify(d.title);
  return withExt ? `${base}.md` : base;
}
function progressFilename(c: ProgressCard, withExt: boolean) {
  const base = `${isoDate(c.periodEnd)}-${slugify(c.title)}`;
  return withExt ? `${base}.md` : base;
}
function teamFilename(t: Team, withExt: boolean) {
  const base = slugify(t.name);
  return withExt ? `${base}.md` : base;
}
